// HintCard — collapsible hint card pojok kanan Build Mode.
// Cyclable via next/prev, dismissable, bisa di-disable all via localStorage.
// Style: `styles/onboarding.css` (.tm-hint-card*).

import { STRINGS } from '@tm/shared'
import { trackEvent } from '../analytics/plausible.js'

const STORAGE_KEY = 'tm.hintcard.disabled'

function isDisabled() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

function render(host, hint, index, total) {
  host.innerHTML = `
    <div class="tm-hint-card" role="complementary" aria-live="polite">
      <div class="tm-hint-card__header">
        <span class="tm-hint-card__icon" aria-hidden="true">${hint.icon || '💡'}</span>
        <h4 class="tm-hint-card__title">${escapeHtml(hint.title)}</h4>
        <button type="button" class="tm-hint-card__close" data-action="dismiss" aria-label="${STRINGS.hintCardDismiss}">×</button>
      </div>
      <p class="tm-hint-card__body">${escapeHtml(hint.body)}</p>
      <div class="tm-hint-card__footer">
        <button type="button" class="tm-hint-card__nav" data-action="prev" aria-label="Previous hint">‹</button>
        <span class="tm-hint-card__counter" aria-label="Hint ${index + 1} of ${total}">${index + 1}/${total}</span>
        <button type="button" class="tm-hint-card__nav" data-action="next" aria-label="Next hint">›</button>
        <button type="button" class="tm-hint-card__disable" data-action="disable">${STRINGS.hintCardDisableAll}</button>
      </div>
    </div>
  `
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function mountHintCard({
  host,
  hints = [],
  initialIndex = 0,
  onDismiss,
  onDisableAll,
} = {}) {
  if (!host) throw new Error('HintCard: host is required')
  if (!Array.isArray(hints) || hints.length === 0) {
    throw new Error('HintCard: hints must be non-empty array')
  }

  let index = initialIndex
  let disabled = isDisabled()
  let currentHints = hints

  function getCurrentHint() {
    return currentHints[index]
  }

  function refresh() {
    if (disabled || currentHints.length === 0) {
      host.innerHTML = ''
      return
    }
    render(host, currentHints[index], index, currentHints.length)
    bind()
  }

  function bind() {
    const el = host.querySelector('.tm-hint-card')
    if (!el) return
    el.querySelector('[data-action="dismiss"]').addEventListener('click', dismiss)
    el.querySelector('[data-action="disable"]').addEventListener('click', disableAll)
    el.querySelector('[data-action="next"]').addEventListener('click', next)
    el.querySelector('[data-action="prev"]').addEventListener('click', prev)
  }

  function next() {
    index = (index + 1) % currentHints.length
    refresh()
    trackEvent('hint_navigated', { hintId: currentHints[index].id, direction: 'next' })
  }

  function prev() {
    index = (index - 1 + currentHints.length) % currentHints.length
    refresh()
    trackEvent('hint_navigated', { hintId: currentHints[index].id, direction: 'prev' })
  }

  function dismiss() {
    const hint = getCurrentHint()
    host.innerHTML = ''
    onDismiss?.(hint.id)
    trackEvent('hint_dismissed', { hintId: hint.id })
  }

  function disableAll() {
    disabled = true
    try {
      localStorage.setItem(STORAGE_KEY, 'true')
    } catch {
      // ignore quota
    }
    host.innerHTML = ''
    onDisableAll?.()
    trackEvent('hint_disabled_all')
  }

  function setHints(newHints) {
    currentHints = newHints
    index = 0
    refresh()
  }

  function destroy() {
    host.innerHTML = ''
  }

  refresh()

  return { next, prev, dismiss, disableAll, setHints, getCurrentHint, destroy }
}
